export interface SurveyQuestion {
  id: string;
  question: string;
  probingQuestions?: string[];
  followUp?: {
    condition: string;
    question: string;
  };
  actions?: {
    [key: string]: string;
  };
  required: boolean;
  category: 'introduction' | 'verification' | 'survey' | 'closing';
}

export const PS_SURVEY_FLOW: SurveyQuestion[] = [
  // Introduction and Verification
  {
    id: 'intro_greeting',
    question: 'Good Morning/Afternoon/Evening, may I speak to [Client Name] please?',
    category: 'introduction',
    required: true,
  },
  {
    id: 'intro_caller',
    question: "My name is Raihana, I'm calling on behalf of Prudential Singapore.",
    category: 'introduction',
    required: true,
  },
  {
    id: 'intro_consent',
    question: 'This call is regarding your signing of policy(s) from Prudential, and if you have a few minutes to spare, we would like to hear from the quality of the service that our FC has provided.',
    category: 'introduction',
    required: true,
  },
  {
    id: 'verification_dob',
    question: 'Thank you. For verification purpose, may I have your full date of birth please?',
    category: 'verification',
    required: true,
  },
  {
    id: 'verification_postal',
    question: 'May I have your postal code please?',
    followUp: {
      condition: 'if_needed',
      question: 'As I might be disclosing some of your policy details, I will need to ensure that I am speaking with the right person.',
    },
    category: 'verification',
    required: true,
  },
  {
    id: 'recording_notice',
    question: 'Thank you [Client Name]. Before I proceed, I wish to inform you that this call is recorded for quality and training purposes.',
    category: 'introduction',
    required: true,
  },

  // Survey Questions
  {
    id: 'q1_documents',
    question: 'Have you received a set of documents that you signed at the point of sale? This could be either email or paper copy.',
    probingQuestions: [
      'Do you recall receiving a set of documents that you had signed on an iPad at the point of sale, for example: PruPlanner, Policy Illustration, Product summary, Your Guide to Life Insurance?',
      'For ILP: Fund information Booklet, Product Highlight Sheets',
      'This could be in your junk mail.',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q1_email_verify',
    question: 'Could I do a quick verification of your email address?',
    followUp: {
      condition: 'if_no_documents',
      question: 'Just wanted to inform you that since your email is correct, the documents should have been emailed to you already, or in a few hours time.',
    },
    category: 'survey',
    required: false,
  },
  {
    id: 'q2_language',
    question: 'What language did your financial consultant use during point of sale?',
    probingQuestions: [
      'From our records, it was documented that you understand English. Therefore, may I confirm that your Financial Consultant did explain the policy to you in English?',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q3_needs_assessment',
    question: 'Before recommending this product, did your financial consultant ask you questions to understand your needs, financial situation, investment objectives and risk appetite?',
    probingQuestions: [
      'Did your financial consultant ask any questions about your annual income, expenses, budget and existing insurance policies?',
      'Did you share with your FC on the purpose of the purchase of the plan? Example saving or protection needs?',
      'Amount you want to set aside?',
      'Income/expenses?',
      'Existing investments or insurance plans that you have?',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q6_accuracy',
    question: 'Was the information provided by you about your financial situation and financial needs documented accurately in the sales documents by your financial consultant?',
    probingQuestions: [
      'Information such as: Income, expenses',
      'Amount of savings',
      'Purpose of the purchase of policy? savings or protection?',
      'Premium and policy term',
      'Existing policies',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q7_understanding',
    question: "Did you understand your financial consultant's explanation on how the recommended policy is suitable for you?",
    probingQuestions: [
      'Are you aware that this is an investment-linked plan?',
      'The Investment-linked policy has coverage for death and the surrender value will depend on the performance of the funds you have purchased. Are you aware of this?',
      'Are you aware that return of the investment may be less or more than the amount you put in?',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q8a_features',
    question: 'In making the product recommendation to you, did your financial consultant explain the features and benefits?',
    probingQuestions: [
      'E.g. the policy has a protection element and Cash Values are non-guaranteed',
      'The Investment-linked policy has coverage for death and the surrender value will depend on the performance of the funds you have purchased. Are you aware of this?',
      'Are you aware that return of the investment may be less or more than the amount you put in?',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q8b_fees',
    question: 'Did your financial consultant explain the fees and charges?',
    probingQuestions: [
      'E.g. Fund Management Fees',
      'Are you aware the Fund Management Fees are paid with the units in your policy?',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q8c_terms',
    question: 'Did your financial consultant explain the terms of the policy?',
    probingQuestions: [
      'E.g. Premium Payment Term and Policy Term',
      'Based on the information documented, may I confirm that you are aware that your policy premium payment term is [Number of Years] and policy term is [Number of Years]?',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q8d_risk',
    question: 'Did your financial consultant explain the investment risk?',
    probingQuestions: [
      'E.g. Risk Classification of the funds you have purchased and Price of the units depend on how the funds perform',
      'Based on the information documented, can I confirm that the funds you purchased are [Fund Names] and the Risk Classification is [Risk Level]?',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q11_freelook',
    question: 'Did your financial consultant inform you about the 14-day free look period?',
    probingQuestions: [
      'Are you aware that you have 14 days (upon receiving your policy documents) to review your policy to see if it is suitable for your needs (to review your purchase decision or make changes)?',
    ],
    followUp: {
      condition: 'if_unaware',
      question: 'Kindly note that upon receiving your policy documents, you would have 14 days to review your policy and make changes if required. At the same time, can I reconfirm that your Financial Consultant didn\'t inform you about the 14 days free look period?',
    },
    category: 'survey',
    required: true,
  },
  {
    id: 'q12_replacement',
    question: 'Was the product purchased meant to replace any of your existing policy(ies)?',
    probingQuestions: [
      'Did you cancel or stop paying any policies to buy this current policy?',
    ],
    followUp: {
      condition: 'if_yes',
      question: 'Could you share with me the reason for replacing your existing policy(ies)?',
    },
    category: 'survey',
    required: true,
  },
  {
    id: 'q13_feedback',
    question: 'Were there any aspects of the sales process or the policy that you would like to provide feedback on for our improvement?',
    category: 'survey',
    required: true,
  },

  // Closing
  {
    id: 'closing',
    question: '[Client Name], we have come to the end of the survey. Thank you for taking the time to speak to me. Should you need any assistance from us, please contact your financial consultant or our Customer Service Hotline at 1800-333-0-333. Thank you once again and have a nice day.',
    category: 'closing',
    required: true,
  },
];

// Raihana's speaking patterns based on the transcript
export const RAIHANA_SPEECH_PATTERNS = {
  fillers: ['Mm', 'Mm-hmm', 'Uh', 'Um', 'Uh...', 'Hmm'],
  acknowledgments: ['Okay', 'Oh, okay', 'Okay, very well', 'I understand', 'I see', 'Thank you for that'],
  transitions: ['So', 'And', 'My next question', 'Next question here'],
  politeness: ['Thank you so much', 'Thank you for your confirmation', 'Glad to know that'],
  clarifications: ['Sorry, sir', 'Oh, okay, sir', 'I repeat my question'],
};

// Helper function to add natural speech patterns
export function addNaturalSpeech(text: string): string {
  const patterns = RAIHANA_SPEECH_PATTERNS;

  // Randomly add fillers at the beginning
  if (Math.random() < 0.3) {
    const filler = patterns.fillers[Math.floor(Math.random() * patterns.fillers.length)];
    text = `${filler}, ${text}`;
  }

  // Add transitions for follow-up questions
  if (text.toLowerCase().includes('next question') || Math.random() < 0.2) {
    const transition = patterns.transitions[Math.floor(Math.random() * patterns.transitions.length)];
    text = `${transition} ${text}`;
  }

  return text;
}

// Function to get appropriate response based on client's answer
export function getAppropriateResponse(questionId: string, clientResponse: string): string {
  const lowerResponse = clientResponse.toLowerCase();

  // Positive responses
  if (lowerResponse.includes('yes') || lowerResponse.includes('correct') || lowerResponse.includes('received')) {
    const acknowledgments = [
      'Okay, very well.',
      'Thank you for the confirmation.',
      'Glad to know that.',
      'Thank you so much for your confirmation.',
    ];
    return acknowledgments[Math.floor(Math.random() * acknowledgments.length)];
  }

  // Negative or uncertain responses
  if (lowerResponse.includes('no') || lowerResponse.includes('not sure') || lowerResponse.includes("don't")) {
    return 'Oh, okay. Let me note that down.';
  }

  // Default acknowledgment
  return 'I understand. Thank you for that information.';
}